import { useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import {
  useAdminPersons,
  useUpdatePersonDisplayName,
  useUpdatePlayerPosition,
  useUpdatePlayerPrice,
  type AdminPersonItem,
  type PersonsFilter,
} from '../queries'
import styles from './PersonsTable.module.css'

type PersonsTableProps = {
  competitionId: string | null
  filter:        PersonsFilter
}

const POSITION_OPTIONS = ['GK', 'DEF', 'MID', 'FWD'] as const

export function PersonsTable({ competitionId, filter }: PersonsTableProps) {
  const hasFilter = !!(filter.teamId || filter.position)
  const { data, isPending, isError, error, isFetching } = useAdminPersons(competitionId, filter)

  if (!competitionId) {
    return <div className={styles.empty}>Selecione uma competição.</div>
  }
  if (!hasFilter) {
    // PersonsFilters already surfaces the hint — render nothing here.
    return null
  }
  if (isPending) return <div className={styles.empty}>Carregando…</div>
  if (isError) {
    const detail = error instanceof ApiError
      ? `${error.status} ${error.errorCode} — ${error.detail}`
      : (error as Error | null)?.message ?? 'Erro desconhecido'
    return <div className={styles.empty}>Erro ao carregar pessoas: <code>{detail}</code></div>
  }
  if (!data)             return <div className={styles.empty}>Erro ao carregar pessoas.</div>
  if (data.length === 0) return <div className={styles.empty}>Nenhuma pessoa corresponde ao filtro.</div>

  return (
    <div className={styles.wrap}>
      <div className={styles.meta}>
        <span className={styles.count}>{data.length} pessoa{data.length === 1 ? '' : 's'}</span>
        {isFetching && <span className={styles.refreshing}>Atualizando…</span>}
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.colName}>Nome (oficial)</th>
            <th className={styles.colDisplay}>Display</th>
            <th className={styles.colTeam}>Time</th>
            <th className={styles.colPosition}>Posição</th>
            <th className={styles.colPrice}>Preço ($)</th>
            <th className={styles.colStatus} />
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <PersonRow key={p.personId} person={p} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const MIN_PRICE = 4
const MAX_PRICE = 15
const PRICE_STEP = 0.1

function PersonRow({ person }: { person: AdminPersonItem }) {
  const [displayDraft, setDisplayDraft] = useState(person.displayName)
  const [priceDraft,   setPriceDraft]   = useState(person.price.toFixed(1))
  const priceInputRef = useRef<HTMLInputElement>(null)
  const updateName  = useUpdatePersonDisplayName()
  const updatePos   = useUpdatePlayerPosition()
  const updatePrice = useUpdatePlayerPrice()

  // Re-sync the inputs when the server values change (after invalidation).
  useEffect(() => {
    setDisplayDraft(person.displayName)
  }, [person.displayName])
  useEffect(() => {
    setPriceDraft(person.price.toFixed(1))
  }, [person.price])

  const noPosition = !person.position
  const isDirty    = displayDraft !== person.displayName
  const canSave    = isDirty && displayDraft.trim().length >= 1

  const priceNum     = Number(priceDraft)
  const priceValid   = Number.isFinite(priceNum) && priceNum >= MIN_PRICE && priceNum <= MAX_PRICE
  const priceDirty   = priceDraft !== person.price.toFixed(1)
  const canSavePrice = priceDirty && priceValid

  function commitName() {
    if (!canSave) {
      setDisplayDraft(person.displayName)
      return
    }
    updateName.mutate({ personId: person.personId, displayName: displayDraft.trim() })
  }

  function handleNameKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      setDisplayDraft(person.displayName)
      e.currentTarget.blur()
    }
  }

  function handlePositionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value
    if (!next || next === person.position) return
    updatePos.mutate({ playerId: person.playerId, position: next })
  }

  function commitPrice() {
    if (!canSavePrice) {
      setPriceDraft(person.price.toFixed(1))   // revert invalid / unchanged input
      return
    }
    updatePrice.mutate({ playerId: person.playerId, price: priceNum })
  }

  function handlePriceKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      setPriceDraft(person.price.toFixed(1))
      e.currentTarget.blur()
    }
  }

  // Nudge the draft by ±0.1 (clamped), keeping the input focused so blur still commits.
  function stepPrice(delta: number) {
    const base = Number.isFinite(priceNum) ? priceNum : person.price
    const next = Math.min(MAX_PRICE, Math.max(MIN_PRICE, Math.round((base + delta) * 10) / 10))
    setPriceDraft(next.toFixed(1))
    priceInputRef.current?.focus()
  }

  return (
    <tr className={clsx(noPosition && styles.rowNoPosition)}>
      <td className={styles.name}>{person.name}</td>
      <td className={styles.displayCell}>
        <input
          className={clsx(styles.displayInput, isDirty && styles.displayInputDirty)}
          value={displayDraft}
          onChange={(e) => setDisplayDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={handleNameKey}
          disabled={updateName.isPending}
          aria-label={`Display name de ${person.name}`}
          spellCheck={false}
        />
      </td>
      <td className={styles.team}>
        <span className={styles.teamCode}>{person.teamCode}</span>
      </td>
      <td className={styles.posCell}>
        <select
          className={clsx(
            styles.posSelect,
            noPosition && styles.posSelectMissing,
          )}
          value={person.position}
          onChange={handlePositionChange}
          disabled={updatePos.isPending}
          aria-label={`Posição de ${person.name}`}
        >
          {noPosition && <option value="" disabled>—</option>}
          {POSITION_OPTIONS.map((pos) => (
            <option key={pos} value={pos}>{pos}</option>
          ))}
        </select>
      </td>
      <td className={styles.priceCell}>
        <div
          className={clsx(
            styles.priceField,
            priceDirty && (priceValid ? styles.priceFieldDirty : styles.priceFieldInvalid),
          )}
        >
          <input
            ref={priceInputRef}
            type="number"
            inputMode="decimal"
            min={MIN_PRICE}
            max={MAX_PRICE}
            step={PRICE_STEP}
            className={styles.priceInput}
            value={priceDraft}
            onChange={(e) => setPriceDraft(e.target.value)}
            onBlur={commitPrice}
            onKeyDown={handlePriceKey}
            disabled={updatePrice.isPending}
            aria-label={`Preço de ${person.name}`}
          />
          <div className={styles.priceStepper}>
            <button
              type="button"
              className={styles.stepBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => stepPrice(PRICE_STEP)}
              disabled={updatePrice.isPending}
              tabIndex={-1}
              aria-label="Aumentar preço"
            >
              <ChevronUp size={11} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={styles.stepBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => stepPrice(-PRICE_STEP)}
              disabled={updatePrice.isPending}
              tabIndex={-1}
              aria-label="Diminuir preço"
            >
              <ChevronDown size={11} aria-hidden="true" />
            </button>
          </div>
        </div>
      </td>
      <td className={styles.statusCell}>
        {noPosition ? (
          <span className={styles.warnPill} title="Sem posição — indisponível na escalação">
            <AlertTriangle size={12} aria-hidden="true" />
            Indisponível
          </span>
        ) : (
          <span className={styles.okPill}>OK</span>
        )}
      </td>
    </tr>
  )
}
